import { Injectable } from '@angular/core';
import { Headers, RequestOptions, Response } from '@angular/http';
import { UploadFile, UploadInput } from 'ngx-uploader';
import { Content } from '../models/content.model';
import { BodyEncoder, Globals } from './../helpers';
import { HttpInterceptor } from './http-error.interceptor';

@Injectable()
export class ContentService {
  private contentServiceUrl: string;

  constructor(private http: HttpInterceptor, private globals: Globals) {
    this.contentServiceUrl = globals.url + 'contents/';
  }

  public putThumbnail(contentId: string) {
    const event: UploadInput = {
      method: 'PUT',
      url: this.contentServiceUrl + contentId + '/thumbnail',
      type: 'uploadAll',
      headers: this.http.jwt(),
    };
    return event;
  }
  public deleteThumbnail(contentId: string) {
    return this.http
      .delete(this.contentServiceUrl + contentId + '/thumbnail')
      .map((response: Response) => response.json());
  }
  public getAll(page?, limit?, filters?) {
    let url = this.contentServiceUrl;
    if (typeof page !== 'undefined' && typeof limit !== 'undefined') {
      url += '?page=' + page + '&limit=' + limit;
    } else {
      url += '?page=0';
    }
    if (typeof filters !== 'undefined') {
      Object.keys(filters).forEach((filter) => {
        url += '&' + filter + '=' + filters[filter];
      });
    }
    return this.http.get(url).map((response: Response) => response.json());
  }

  public getDistinctFilters() {
    return this.http
      .get(this.contentServiceUrl + 'distinctFilters')
      .map((response: Response) => response.json());
  }
  public getContentCount(filters?) {
    let url = this.contentServiceUrl;
    if (typeof filters !== 'undefined') {
      Object.keys(filters).forEach((filter, i) => {
        if (i == 0) { url += '?' + filter + '=' + filters[filter]; } else { url += '&' + filter + '=' + filters[filter]; }
      });
    }
    return this.http.get(url).map((response: Response) => response.json());
  }

  public subscribeDevicesToContent(contentId: string, deviceIds: string[]) {
    const subscribeDeviceIds = { subscribeDeviceIds: deviceIds };
    return this.http
      .put(
        this.contentServiceUrl + contentId + '/subscribe',
        subscribeDeviceIds,
      )
      .map((response: Response) => response.json());
  }
  public unsubscribeDevicesToContent(contentId: string, deviceIds: string[]) {
    const unsubscribeDeviceIds = { unsubscribeDeviceIds: deviceIds };
    return this.http
      .put(
        this.contentServiceUrl + contentId + '/unsubscribe',
        unsubscribeDeviceIds,
      )
      .map((response: Response) => response.json());
  }

  public getById(contentId: string) {
    return this.http
      .get(this.contentServiceUrl + contentId)
      .map((response: Response) => response.json());
  }
  public create(content) {
    return this.http
      .post(this.contentServiceUrl, content)
      .map((response: Response) => response.json());
  }
  public update(contentId: string, content: Content) {
    return this.http
      .put(this.contentServiceUrl + contentId, content)
      .map((response: Response) => response.json());
  }

  public delete(contentId: string, deleteContentRecursive?: boolean) {
    let urlaux = this.contentServiceUrl + contentId;
    if (deleteContentRecursive) { urlaux += '?deleteContentRecursive=' + deleteContentRecursive; }
    return this.http
      .delete(urlaux).map((response: Response) => response.json());
  }

  public getContentNodes(contentId: string) {
    return this.http
      .get(this.contentServiceUrl + contentId + '/nodes')
      .map((response: Response) => response.json());
  }

  public getSubscribedDevices(contentId: string, page?, limit?, filters?) {
    let url = this.contentServiceUrl + contentId + '/subscribedDevices';
    if (typeof page == 'number' && typeof limit == 'number') {
      url = this.prepareUrlFilters(url, { ...filters, page, limit });
    } else {
      url = this.prepareUrlFilters(url, filters);
    }
    return this.http.get(url).map((response: Response) => response.json());
  }
  public getDevicesToSubscribe(contentId: string, page?, limit?, filters?) {
    let url = this.contentServiceUrl + contentId + '/notSubscribedDevices';
    if (typeof page == 'number' && typeof limit == 'number') {
      url = this.prepareUrlFilters(url, { ...filters, page, limit });
    } else {
      url = this.prepareUrlFilters(url, filters);
    }
    return this.http.get(url).map((response: Response) => response.json());
  }

  public createNode(contentId: string, nodePath: string) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/nodes', {
        op: 'create',
        path: nodePath,
      })
      .map((response: Response) => response.json());
  }
  public removeNode(contentId: string, nodePath: string) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/nodes', {
        op: 'delete',
        path: nodePath,
      })
      .map((response: Response) => response.json());
  }
  public moveNodes(contentId: string, path: string, folderDest: string) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/nodes', {
        op: 'move',
        path,
        folderDest,
      })
      .map((response: Response) => response.json());
  }

  public enterMaintainenceMode(contentId: string) {
    return this.http
      .put(this.contentServiceUrl + contentId + '/maintenance', null)
      .map((response: Response) => response.json());
  }

  public leaveMaintainenceMode(contentId: string) {
    return this.http
      .delete(this.contentServiceUrl + contentId + '/maintenance')
      .map((response: Response) => response.json());
  }
  public addCollaborator(contentId: string, userId: string) {
    return this.http
      .put(this.contentServiceUrl + contentId + '/collaborators/' + userId, {})
      .map((response: Response) => response.json());
  }
  public removeCollaborator(contentId: string, userId: string) {
    return this.http
      .delete(this.contentServiceUrl + contentId + '/collaborators/' + userId)
      .map((response: Response) => response.json());
  }

  public uploadFile(contentId: string, body?) {
    const event: UploadInput = {
      url: this.contentServiceUrl + contentId + '/upload',
      method: 'POST',
      type: 'uploadAll',
      headers: this.http.jwt(),
    };
    if (body && Object.keys(body).length) { event.data = body; }
    return event;
  }

  public extractFile(contentId: string, file: any) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/extract', { file })
      .map((response: Response) => response.json());
  }
  public checkValidZimUrl(url: string) {
    return this.http
      .post(this.contentServiceUrl + 'check-url', { url })
      .map((response: Response) => response.json());
  }
  public convertToStructured(contentId: string, body: any) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/convert-to-structured', body)
      .map((response: Response) => response.json());
  }
  public moveNode(contentId: string, path: string, folderDest: string) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/nodes', {
        op: 'move',
        path,
        folderDest,
      })
      .map((response: Response) => response.json());
  }
  public renameFile(contentId: string, path: string, newName: string) {
    return this.http
      .post(this.contentServiceUrl + contentId + '/nodes', {
        op: 'rename',
        path,
        newName,
      })
      .map((response: Response) => response.json());
  }

  public export(contentId: string) {
    return this.http
      .get(this.contentServiceUrl + contentId + '/export')
      .map((response: Response) => response.json());
  }

  public getExportList() {
    return this.http
      .get(this.contentServiceUrl + 'export-list')
      .map((response: Response) => response.json());
  }

  private prepareUrlFilters(url, filters) {
    const params: URLSearchParams = new URLSearchParams();
    if (!filters || !Object.keys(filters).length) { return url; }
    Object.entries(filters).forEach(([key, value]) => {
      params.append(key, value.toString());
    });
    const paramsStr = params.toString();
    if (paramsStr) { url += '?' + paramsStr; }
    return url;
  }
}
